// Kosten über alle Mandanten, je Monat und je Endpoint-Art: Requesty-Ausgaben
// des Plattform-Keys, des eigenen Keys der Flow-Modelle und die Summe der
// Instanz (`cost_usd`, eigene Endpoints).
// Die Zahlen stammen aus „Aktualisieren" — gezählt wird bei Requesty und in der
// Instanz, nicht hier.

import { useMutation, useQuery } from "@tanstack/react-query";
import { get, post } from "../api/client";
import { keys } from "../api/queries";
import type { CostsResponse } from "../api/types";
import { PageHead } from "../app/page-head";
import { currentMonth, eur, fmtUsd } from "../lib/format";
import { errorText, stateOf } from "../lib/labels";
import { useNavigate, useSearchParams } from "../router/router";
import { Badge, ErrorNote } from "../ui/badge";
import { Card } from "../ui/card";
import { Input } from "../ui/field";
import { toast } from "../ui/toaster";

/** Die Summen der Tabelle: Plattform-Key, Flow-Key, Instanz, Lizenzen. */
export function costSums(tenants: CostsResponse["tenants"]): { key: number; flows: number; instance: number; seats: number } {
  let key = 0, flows = 0, instance = 0, seats = 0;
  for (const t of tenants) {
    key += t.cost?.platform_key_spend_usd ?? 0;
    flows += t.cost?.flow_key_spend_usd ?? 0;
    instance += t.cost?.instance_usd ?? 0;
    seats += t.seat_price?.total_eur ?? 0;
  }
  return { key, flows, instance, seats };
}

export function CostsView() {
  const navigate = useNavigate();
  const month = useSearchParams().get("month") ?? currentMonth();
  const query = useQuery({ queryKey: keys.costs(month), queryFn: () => get<CostsResponse>(`/costs?month=${encodeURIComponent(month)}`) });
  const refresh = useMutation({
    mutationFn: async () => {
      await post("/tenants/refresh", { month });
      await query.refetch();
    },
    onError: (err) => toast(errorText(err), "error"),
  });
  const res = query.data;
  const sums = res ? costSums(res.tenants) : null;
  return (
    <>
      <PageHead
        title="Kosten"
        hint="Plattform-Key: Ausgaben bei Requesty im laufenden Monat. Flows: der eigene Requesty-Key der Flow-Modelle (eigenes Limit). Instanz: Summe der Turn-Kosten im Monat (eigene Endpoints, Preise je Modell)."
        actions={(
          <>
            <Input name="month" type="month" value={month} aria-label="Monat" onChange={(e) => { if (e.target.value) navigate(`/costs?month=${e.target.value}`); }} />
            <button type="button" className="pf-btn" disabled={refresh.isPending} onClick={() => refresh.mutate()}>Aktualisieren</button>
          </>
        )}
      />
      {query.isError ? <ErrorNote error={query.error} /> : null}
      {res && sums ? (
        <Card title={`Monat ${res.month}`} flush>
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead><tr><th>Mandant</th><th>Endpoints</th><th className="pf-num">Plattform-Key</th><th className="pf-num">Flows</th><th className="pf-num">Instanz</th><th className="pf-num">Lizenzen / Monat</th></tr></thead>
              <tbody>
                {res.tenants.length ? res.tenants.map((t) => {
                  const c = t.cost ?? {};
                  const [st, tone] = stateOf(t.state);
                  const budget = c.platform_key_budget_usd;
                  const p = budget ? (100 * (c.platform_key_spend_usd ?? 0)) / budget : null;
                  const flowBudget = c.flow_key_budget_usd;
                  const fp = flowBudget ? (100 * (c.flow_key_spend_usd ?? 0)) / flowBudget : null;
                  const href = `/tenants/${t.id}/costs`;
                  return (
                    <tr key={t.id} data-href={href} onClick={() => navigate(href)}>
                      <td><strong>{t.name || t.slug}</strong> <Badge tone={tone}>{st}</Badge></td>
                      <td>{[t.models.platform.enabled ? "Plattform" : "", t.models.own.enabled ? "eigener" : ""].filter(Boolean).join(" + ") || "—"}</td>
                      <td className="pf-num">{t.cost ? fmtUsd(c.platform_key_spend_usd) : "—"}{p !== null ? <div className="pf-sub">{p.toFixed(0)} % von {fmtUsd(budget)}</div> : null}</td>
                      <td className="pf-num">{t.cost && c.flow_key_spend_usd !== undefined ? fmtUsd(c.flow_key_spend_usd) : "—"}{fp !== null ? <div className="pf-sub">{fp.toFixed(0)} % von {fmtUsd(flowBudget)}</div> : null}</td>
                      <td className="pf-num">{t.cost ? fmtUsd(c.instance_usd) : "—"}{c.unpriced_models?.length ? <div className="pf-sub">ohne Preis: {c.unpriced_models.join(", ")}</div> : null}</td>
                      <td className="pf-num">{t.seat_price?.total_eur ? eur(t.seat_price.total_eur) : "—"}{t.seat_price?.unpriced ? <div className="pf-sub">Lizenz ohne Preis</div> : null}</td>
                    </tr>
                  );
                }) : <tr><td colSpan={6} className="pf-empty">Keine Mandanten</td></tr>}
                <tr>
                  <td><strong>Summe</strong></td><td />
                  <td className="pf-num"><strong>{fmtUsd(sums.key)}</strong></td>
                  <td className="pf-num"><strong>{fmtUsd(sums.flows)}</strong></td>
                  <td className="pf-num"><strong>{fmtUsd(sums.instance)}</strong></td>
                  <td className="pf-num"><strong>{eur(sums.seats)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </>
  );
}
